import { ExtendedDriver, ExtendedTractor } from '@hooks/useDriverTractors.types';
import { IDriverDropdownSelector } from './types';
import { useRef, useState } from 'react';
import { Slick } from '@/Infrastructure/slick';
import DriverDataGrid from '@components/DataGrid/Driver/Grid';
import TractorDataGrid from '@components/DataGrid/Tractor/Grid';
import { GridFilterModel, useGridApiRef } from '@mui/x-data-grid-pro';

import styles from './DriverDropdown.module.css';

/**
 *	Component for selecting a Driver/Tractor to assign to a leg by Tractor first
 */
export default function TractorSelector({ options, onChange, ...props }: IDriverDropdownSelector<ExtendedTractor>) {
	/** MUI Grid API Ref to forward any filters applied to the Tractor grid on to the Driver grid by default */
	const tractorGridApiRef = useGridApiRef();
	/** React-Slick Ref used for programatically changing the current slide */
	const slickRef = useRef<Slick>(null);
	/** Need to retain the selected tractor in the case that we also need to select a driver */
	const [tempTractor, setTempTractor] = useState<ExtendedTractor | undefined>();

	const [filterModel, setFilterModel] = useState<GridFilterModel | undefined>();

	/** Callback method when a Tractor is selected from the Tractor Data Grid */
	function onTractorSelect(value: ExtendedTractor | null) {
		const defaultDriver = value?.drivers?.find((d) => d.id === value.drvId);
		/** If the Tractor is known to have more than one driver allow them to select. Or if the Tractor has a single Driver who is not the default. */
		if ((value?.drivers && value.drivers.length > 1) || (value?.drivers && value.drivers.length === 1 && !defaultDriver)) {
			setTempTractor(value);
			setFilterModel(tractorGridApiRef.current?.exportState().filter?.filterModel);
			slickRef.current?.slickNext();
		} else if (value?.drvId && value.drivers?.[0]) {
			onChange({
				driver: value.drivers[0],
				tractorId: value.id
			});
		}
	}

	/** Callback method when a Driver is selected from the Driver Data Grid in the event there were multiple available drivers */
	function onDriverSelect(value: ExtendedDriver | null) {
		if (value && tempTractor) {
			onChange({
				driver: value,
				tractorId: tempTractor.id
			});
		}
	}

	return (
		<Slick
			className={styles.slick}
			swipe={false}
			prevArrow={<></>}
			nextArrow={<></>}
			infinite={false}
			centerPadding='0px'
			lazyLoad='ondemand'
			ref={slickRef}
			speed={900}
			afterChange={(index) => {
				if (index === 0) {
					/** If the user comes back to the initial Grid, clear the temp Tractor we were storing  */
					setTempTractor(undefined);
				}
			}}
		>
			<div>
				<div className={styles['grid-container']}>
					<TractorDataGrid
						rows={options}
						onRowSelect={onTractorSelect}
						displayCurrentDriver
						gridApiRef={tractorGridApiRef}
						legRequiresHazmat={props.legRequiresHazmatCertification}
					/>
				</div>
			</div>
			<div>
				<div className={styles['grid-container']}>
					<DriverDataGrid
						key={`driver-grid-${tempTractor?.id ?? 'unknown'}`}
						rows={tempTractor?.drivers ?? []}
						onRowSelect={onDriverSelect}
						defaultFilterModel={filterModel}
						slickRef={slickRef}
					/>
				</div>
			</div>
		</Slick>
	);
}
